import {
  ChangeEvent, forwardRef, useMemo, useEffect, memo,
} from 'react';
import { StandardTextFieldProps } from '@mui/material/TextField';
import { debounce } from 'lodash';
import { useForwardedRef, usePrevious } from '../../hooks';
import TextField from '../TextField';
import InputAdornment from '../InputAdornment';
import { SearchIcon } from '../../icons';
import { S2, S3, S4 } from '../../theme';
import TablePreserverCommonInitialTableValues
  from '../../hooks/tablePreservation/TablePreserverCommonInitialTableValues';
import { mergeSX } from '../../services/helpers';

export interface ISearchableTextFieldProps extends Omit<
  StandardTextFieldProps, 'value' | 'onChange' | 'color'
> {
  value?: string;
  onChange: (value: string | undefined) => void;
  debounceDelay?: number;
  color?: string;
  backgroundColor?: string;
  paddingRight?: string | number;
  hideBorder?: boolean;
}

interface IBuildSXParams {
  color?: string;
  backgroundColor: string;
  paddingRight?: string | number;
  hideBorder: boolean;
}

const buildSX = ({
  color, backgroundColor, paddingRight, hideBorder,
}: IBuildSXParams) => ({
  '.MuiOutlinedInput-root': {
    borderRadius: 12,
    height: 48,
    color,
    backgroundColor,
    paddingLeft: 3,
    paddingRight,
    '& .MuiOutlinedInput-notchedOutline': {
      border: hideBorder ? 'none' : `1px solid ${S3}`,
    },
    '& .MuiInputAdornment-root': {
      padding: 0,
    },
    '& input': {
      paddingLeft: 0,
    },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      border: hideBorder ? 'none' : `1px solid ${S3}`,
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
      border: hideBorder ? 'none' : `1px solid ${S3}`,
    },
    '&.MuiInputBase-sizeSmall': {
      height: 34,
    },
  },
});

const SearchableTextField = forwardRef<
  HTMLElement, ISearchableTextFieldProps
>((props, forwardedRef) => {
  const {
    value,
    onChange,
    placeholder,
    debounceDelay = 300,
    inputRef,
    sx,
    color,
    backgroundColor = S2,
    paddingRight,
    hideBorder = false,
    ...restProps
  } = props;

  const ref = useForwardedRef<HTMLInputElement>(forwardedRef || inputRef);

  const previousValue = usePrevious(value);

  const handleDebouncedChange = useMemo(
    () => debounce((e: ChangeEvent<HTMLInputElement>) => {
      // use logical OR so the value will be either default or a non-empty string
      onChange(e.target.value || TablePreserverCommonInitialTableValues.searchQuery);
    }, debounceDelay),
    [onChange, debounceDelay],
  );

  useEffect(() => {
    // previous and current values will be equal on uncontrolled components
    if (!ref.current || previousValue === value) {
      return;
    }
    ref.current.value = value ?? '';
  }, [ref, previousValue, value]);

  return (
    <TextField
      autoComplete="off"
      sx={mergeSX(buildSX({
        color, backgroundColor, paddingRight, hideBorder,
      }), sx)}
      InputProps={{
        startAdornment: (
          <InputAdornment position="start">
            <SearchIcon color={S4} width={24} height={24} />
          </InputAdornment>
        ),
      }}
      placeholder={placeholder}
      autoFocus
      inputRef={ref}
      onChange={handleDebouncedChange}
      {...restProps}
    />
  );
});

export default memo(SearchableTextField);
